import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, ChevronDown, ChevronUp, Upload, X } from 'lucide-react';
import { authApi } from '../../helpers/auth';
import { adminApi } from '../../helpers/adminApi';
import { API_URL } from '../../helpers/api';
import WysiwygEditor from '../../components/WysiwygEditor';

const AdminMenuPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadPage();
  }, []);

  const loadPage = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/pages.php?page=menu-special');
      if (!response.ok) throw new Error('Erreur de chargement');
      const data = await response.json();
      // Migration: convert old single image format to images array
      if (data.image && !data.images) {
        data.images = [data.image];
        delete data.image;
      }
      if (!data.images) {
        data.images = [];
      }
      setPageData(data);
    } catch (err) {
      setError('Erreur lors du chargement');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      const token = authApi.getToken();
      const response = await fetch('/api/pages.php?page=menu-special', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(pageData),
      });
      if (!response.ok) throw new Error('Erreur de sauvegarde');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setPageData(prev => ({ ...prev, [field]: value }));
  };

  const updateNested = (path, value) => {
    setPageData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = isNaN(keys[i]) ? keys[i] : parseInt(keys[i]);
        obj = obj[key];
      }
      const lastKey = isNaN(keys[keys.length - 1]) ? keys[keys.length - 1] : parseInt(keys[keys.length - 1]);
      obj[lastKey] = value;
      return updated;
    });
  };

  const addSection = () => {
    setPageData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.sections.push({ title: '', content: '' });
      return updated;
    });
  };

  const removeSection = (index) => {
    setPageData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.sections.splice(index, 1);
      return updated;
    });
  };

  const moveSection = (index, direction) => {
    setPageData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= updated.sections.length) return prev;
      const temp = updated.sections[index];
      updated.sections[index] = updated.sections[newIndex];
      updated.sections[newIndex] = temp;
      return updated;
    });
  };

  // Multi-image upload
  const handleImagesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploadPromises = files.map(file => adminApi.uploadFile(file));
      const results = await Promise.all(uploadPromises);
      const newImages = results.flat().map(uploaded => ({
        url: uploaded.url,
        name: uploaded.name,
        mime: uploaded.mime,
      }));
      setPageData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...newImages],
      }));
    } catch (err) {
      setError('Erreur lors de l\'upload');
      console.error(err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (index) => {
    setPageData(prev => {
      const images = [...(prev.images || [])];
      images.splice(index, 1);
      return { ...prev, images };
    });
  };

  const moveImage = (index, direction) => {
    setPageData(prev => {
      const images = [...(prev.images || [])];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= images.length) return prev;
      [images[index], images[newIndex]] = [images[newIndex], images[index]];
      return { ...prev, images };
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Page Exposition</h2>
          <small className="text-muted">
            Gérez le contenu de la page exposition et son affichage dans le menu
          </small>
        </div>
        <div className="d-flex align-items-center gap-2">
          {success && <span className="text-success fw-bold">Sauvegardé !</span>}
          {error && <span className="text-danger">{error}</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !pageData}>
            <Save size={16} className="me-2" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {pageData && (
        <>
          {/* Activation & Menu */}
          <div className="card mb-4 border-primary">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Affichage dans le menu</h5>
            </div>
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-bold">Nom dans le menu</label>
                    <input
                      type="text"
                      className="form-control"
                      value={pageData.menuName || ''}
                      onChange={e => updateField('menuName', e.target.value)}
                      placeholder="Ex: Nou an péyi révé"
                    />
                    <small className="text-muted">Le texte qui apparaîtra dans la barre de navigation</small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check form-switch" style={{ transform: 'scale(1.3)', marginLeft: '10px' }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="menuActive"
                      checked={pageData.active || false}
                      onChange={e => updateField('active', e.target.checked)}
                    />
                    <label className="form-check-label fw-bold" htmlFor="menuActive">
                      {pageData.active ? 'Visible dans le menu' : 'Masqué du menu'}
                    </label>
                  </div>
                </div>
              </div>
              {pageData.active && (
                <div className="alert alert-info mb-0 mt-2">
                  Le lien <strong>"{pageData.menuName || '...'}"</strong> sera affiché dans le menu principal et pointera automatiquement vers <code>/exposition-speciale</code>
                </div>
              )}
            </div>
          </div>

          {/* Hero */}
          <div className="card mb-4">
            <div className="card-header"><h5 className="mb-0">En-tête</h5></div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">Titre de l'exposition</label>
                <input
                  type="text"
                  className="form-control"
                  value={pageData.hero?.title || ''}
                  onChange={e => updateNested('hero.title', e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Sous-titre (commissaires, etc.)</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={pageData.hero?.subtitle || ''}
                  onChange={e => updateNested('hero.subtitle', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="card mb-4">
            <div className="card-header"><h5 className="mb-0">Dates</h5></div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">Période (ex: "Du 22 mai au 29 août 2026")</label>
                <input
                  type="text"
                  className="form-control"
                  value={pageData.dates || ''}
                  onChange={e => updateField('dates', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Images Upload - Multi */}
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Images</h5>
              <label className="btn btn-sm btn-outline-primary mb-0">
                <Upload size={16} className="me-1" />
                {uploading ? 'Upload en cours...' : 'Ajouter des images'}
                <input
                  type="file"
                  className="d-none"
                  accept="image/*"
                  multiple
                  onChange={handleImagesUpload}
                  disabled={uploading}
                />
              </label>
            </div>
            <div className="card-body">
              {(!pageData.images || pageData.images.length === 0) ? (
                <div className="border rounded p-4 text-center bg-light">
                  <Upload size={32} className="text-muted mb-2" />
                  <p className="text-muted mb-2">Aucune image. Uploadez les affiches ou visuels de l'exposition.</p>
                  <label className="btn btn-outline-primary">
                    <Upload size={16} className="me-2" />
                    {uploading ? 'Upload en cours...' : 'Choisir des images'}
                    <input
                      type="file"
                      className="d-none"
                      accept="image/*"
                      multiple
                      onChange={handleImagesUpload}
                      disabled={uploading}
                    />
                  </label>
                </div>
              ) : (
                <div className="row g-3">
                  {pageData.images.map((img, index) => (
                    <div key={index} className="col-md-4 col-sm-6">
                      <div className="card h-100">
                        <div className="position-relative">
                          <img
                            src={`${API_URL}${img.url}`}
                            alt={img.name || `Image ${index + 1}`}
                            className="card-img-top"
                            style={{ height: '180px', objectFit: 'cover' }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                            onClick={() => removeImage(index)}
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <div className="card-body p-2">
                          <small className="text-truncate d-block mb-2">{img.name}</small>
                          <div className="btn-group btn-group-sm w-100">
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => moveImage(index, -1)}
                              disabled={index === 0}
                            >
                              &larr;
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => moveImage(index, 1)}
                              disabled={index === pageData.images.length - 1}
                            >
                              &rarr;
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sections de contenu */}
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Paragraphes de description</h5>
              <button className="btn btn-sm btn-outline-primary" onClick={addSection}>
                <Plus size={16} className="me-1" /> Ajouter un paragraphe
              </button>
            </div>
            <div className="card-body">
              {pageData.sections?.map((section, index) => (
                <div key={index} className="border rounded p-3 mb-3 bg-light">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="badge bg-secondary">Paragraphe {index + 1}</span>
                    <div className="d-flex gap-1">
                      <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => moveSection(index, -1)} disabled={index === 0}>
                        <ChevronUp size={14} />
                      </button>
                      <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => moveSection(index, 1)} disabled={index === pageData.sections.length - 1}>
                        <ChevronDown size={14} />
                      </button>
                      <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeSection(index)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="form-label small fw-bold">Titre (optionnel)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={section.title || ''}
                      onChange={e => updateNested(`sections.${index}.title`, e.target.value)}
                      placeholder="Laisser vide si pas de sous-titre"
                    />
                  </div>
                  <div>
                    <label className="form-label small fw-bold">Contenu</label>
                    <WysiwygEditor
                      value={section.content || ''}
                      onChange={html => updateNested(`sections.${index}.content`, html)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Artistes */}
          <div className="card mb-4">
            <div className="card-header"><h5 className="mb-0">Artistes</h5></div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">Liste des artistes</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={pageData.artists || ''}
                  onChange={e => updateField('artists', e.target.value)}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminMenuPage;
